/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Pagination from 'react-js-pagination';
import baseUrl from '../BaseUrl';
import { watchParam } from '../../context/UserSegmentContext';
import ExportButton from './ExportButton';
import EmptyState from './states/EmptyState';
import ErrorState from './states/ErrorState';

/**
 * WAU users popup — opened from the WAU card. Lists the users behind the WAU
 * number (7 days ending at the anchor date) with how many days before the
 * anchor they registered (users.registered_time), newest registrations first.
 * Optional "Registered within N days" and name/email search, paginated + CSV.
 *
 * Props: { open, anchorDate, wau, segment, onClose }
 */
const LIMIT = 20;
const FILTER_DEBOUNCE_MS = 400;

const EXPORT_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'registered_time', label: 'Registered on' },
  { key: 'days_since_registration', label: 'Days since registration' },
  { key: 'last_active', label: 'Last active in week (UTC)' },
];

const asDate = (d) => (d ? String(d).split('-').reverse().join('/') : '-');

// 'YYYY-MM-DD HH:MM:SS' → 'DD/MM/YYYY HH:MM'
const asDateTime = (v) => {
  if (!v) return '-';
  const [date, time = ''] = String(v).split(' ');
  return `${asDate(date)}${time ? ` ${time.slice(0, 5)}` : ''}`;
};

export const daysLabel = (n) => {
  if (n == null) return '-';
  if (n === 0) return 'Today';
  return `${n} ${Math.abs(n) === 1 ? 'day' : 'days'}`;
};

const WauUsersModal = ({ open, anchorDate, wau, segment, onClose }) => {
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);
  const [maxDays, setMaxDays] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      const now = new Date();
      if (expire * 1000 < now.getTime()) {
        const r = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${r.data.accessToken}`;
        setToken(r.data.accessToken);
        setExpire(jwtDecode(r.data.accessToken).exp);
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  const buildUrl = useCallback(
    (pg, exportAll) => {
      const parts = [];
      if (anchorDate) parts.push(`anchor_date=${anchorDate}`);
      if (maxDays !== '') parts.push(`max_days=${maxDays}`);
      if (search.trim()) parts.push(`search=${encodeURIComponent(search.trim())}`);
      parts.push(exportAll ? 'export=1' : `page=${pg}&limit=${LIMIT}`);
      return `${baseUrl}/admin/api/get-wau-users?${parts.join('&')}${watchParam(segment)}`;
    },
    [anchorDate, maxDays, search, segment]
  );

  const load = useCallback(
    async (pg) => {
      setLoading(true);
      setError('');
      try {
        const res = await axiosJWT.get(buildUrl(pg, false));
        setRows(res.data.data || []);
        setTotalItem(res.data.totalItem || 0);
      } catch {
        setError('We couldn’t load the WAU users. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [buildUrl]
  );

  // Reset the filters each time the popup opens for a (new) anchor/segment.
  useEffect(() => {
    if (open) {
      setMaxDays('');
      setSearch('');
    }
  }, [open, anchorDate, segment]);

  // (Re)load page 1 on open and whenever a filter changes (debounced for typing).
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      setPage(1);
      load(1);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [open, buildUrl]);

  if (!open) return null;

  const handlePage = (pg) => {
    setPage(pg);
    load(pg);
  };

  const handleMaxDays = (e) => {
    const v = e.target.value;
    if (v === '' || /^\d{1,5}$/.test(v)) setMaxDays(v);
  };

  const fetchExportRows = async () => {
    const res = await axiosJWT.get(buildUrl(1, true));
    return res.data.data || [];
  };

  const filtered = maxDays !== '' || search.trim() !== '';
  const exportName = `wau-users-${anchorDate || 'today'}${maxDays !== '' ? `-within-${maxDays}-days` : ''}.csv`;

  return (
    <div className="au-modal-bg" data-testid="wau-modal" onClick={onClose}>
      <div className="au-modal au-modal--xwide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="au-modal-close" onClick={onClose} aria-label="Close" title="Close">×</button>
        <h2 className="au-modal-title">WAU users · 7 days ending {asDate(anchorDate)}</h2>
        <p className="auv-caption">
          Days since registration = days from the user’s registration date (registered_time) to {asDate(anchorDate)}.
          Users with no registration date are listed last.
        </p>

        <div className="wau-filters">
          <label className="wau-within">
            <span>Registered within</span>
            <input
              type="text"
              inputMode="numeric"
              className="au-input wau-within__input"
              placeholder="any"
              value={maxDays}
              onChange={handleMaxDays}
              aria-label="Registered within days"
              data-testid="wau-max-days"
            />
            <span>days</span>
          </label>
          <input
            type="search"
            className="au-input wau-search"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search name or email"
            data-testid="wau-search"
          />
        </div>

        <div className="auv-modal-toolbar">
          <span className="auv-count" data-testid="wau-count">
            {Number(totalItem).toLocaleString()} users
            {filtered && wau != null ? ` of ${Number(wau).toLocaleString()} WAU` : ''}
          </span>
          <span className="au-spacer" />
          <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExportRows} filename={exportName} disabled={!totalItem} />
        </div>

        {loading ? (
          <p className="au-modal-empty">Loading…</p>
        ) : error ? (
          <ErrorState message={error} onRetry={() => load(page)} />
        ) : rows.length === 0 ? (
          <EmptyState title="No users" message={filtered ? 'No WAU users match these filters.' : 'No users were active in this week.'} />
        ) : (
          <>
            <div className="wau-table-wrap">
              <table className="au-modal-table" data-testid="wau-table">
                <thead>
                  <tr>
                    <th>ID</th><th>Name</th><th>Email</th><th>Registered on</th>
                    <th className="wau-num">Days since registration</th><th>Last active (UTC)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td data-label="ID" className="tnum">{r.id}</td>
                      <td data-label="Name">{r.name || '-'}</td>
                      <td data-label="Email" className="wau-email">{r.email || '-'}</td>
                      <td data-label="Registered on" className="tnum">{asDateTime(r.registered_time)}</td>
                      <td data-label="Days since registration" className="au-num-blue wau-num tnum">{daysLabel(r.days_since_registration)}</td>
                      <td data-label="Last active (UTC)" className="tnum">{asDateTime(r.last_active)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalItem > LIMIT && (
              <div className="auv-pagination">
                <Pagination
                  activePage={page}
                  itemsCountPerPage={LIMIT}
                  totalItemsCount={totalItem}
                  pageRangeDisplayed={5}
                  onChange={handlePage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WauUsersModal;
